import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
} from "@mui/material";
import { Close as CloseIcon, Edit as EditIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useUpdateEtablissement } from "../../../api/etablissement";
import { CustomLoader } from "../../../components/CustomLoader/CustomLoader";

interface ModalEditEtablissementProps {
  open: boolean;
  onClose: () => void;
  etablissement: {
    id: number;
    name: string;
  } | null;
  onSuccess?: () => void;
}

export const ModalEditEtablissement: React.FC<ModalEditEtablissementProps> = ({
  open,
  onClose,
  etablissement,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");

  const { mutate: updateEtablissement, isPending } = useUpdateEtablissement(
    etablissement?.id || 0,
    () => {
      handleClose();
      onSuccess?.();
    },
    onSuccess
  );

  useEffect(() => {
    if (etablissement) {
      setName(etablissement.name);
    }
  }, [etablissement]);

  const handleClose = () => {
    setName("");
    setNameError("");
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!name.trim()) {
      setNameError(t("etablissement.nameRequired", "Name is required"));
      return;
    }
    
    if (name.trim().length < 2) {
      setNameError(t("etablissement.nameMinLength", "Name must be at least 2 characters"));
      return;
    }

    setNameError("");
    updateEtablissement({ name: name.trim() });
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (nameError) {
      setNameError("");
    }
  };

  return (
    <>
      {isPending && <CustomLoader isActive={isPending} />}
      
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "primary.main",
            color: "white",
            py: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EditIcon />
            <Typography variant="h6" component="span">
              {t("etablissement.edit", "Edit Etablissement")}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ color: "white" }}
            disabled={isPending}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t("etablissement.editDescription", "Update the etablissement name")}
              </Typography>
              
              <TextField
                label={t("etablissement.name", "Name")}
                value={name}
                onChange={handleNameChange}
                error={!!nameError}
                helperText={nameError}
                fullWidth
                required
                variant="outlined"
                placeholder={t("etablissement.namePlaceholder", "Enter etablissement name")}
                disabled={isPending}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                  },
                }}
              />
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              disabled={isPending}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                minWidth: "80px",
              }}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isPending || !name.trim() || name.trim() === etablissement?.name}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                minWidth: "80px",
              }}
            >
              {t("common.save", "Save")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};